import { PUBLIC_RECAPTCHA_SITE_KEY, PUBLIC_RECAPTCHA_ENABLE } from '$env/static/public';

import { writable, get } from 'svelte/store';

import { createVisibilityStore as createLoadedStore } from '@shared/libs/tools';

function createRecaptchaModel() {
  const { subscribe, update } = writable<Record<string, string>>({});

  const enabled = PUBLIC_RECAPTCHA_ENABLE === 'true';
  const widgets = new Map<string, number>();

  let script: HTMLScriptElement | null = null;

  function initRecaptchaScript() {
    if (script) return;

    script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      recaptchaLoaded.show();
    };
  }

  function initRecaptchaContainer(containerId: string) {
    if (!enabled) {
      return;
    }

    if (!get(recaptchaLoaded)) {
      return;
    }

    window.grecaptcha.ready(() => {
      const widgetId = window.grecaptcha.render(containerId, {
        sitekey: PUBLIC_RECAPTCHA_SITE_KEY,
        size: 'invisible',
        callback(token: string) {
          update((current) => ({
            ...current,
            [containerId]: token,
          }));
        },
      });

      widgets.set(containerId, widgetId);
    });
  }

  function executeRecaptchaContainer(containerId: string) {
    if (!enabled) return;

    const widgetId = widgets.get(containerId);

    if (widgetId === undefined) {
      return;
    }

    const id = window.grecaptcha.execute(widgetId);

    return id;
  }

  function destroyRecaptchaWidget(containerId: string) {
    const widgetId = widgets.get(containerId);

    if (widgetId !== undefined) {
      window.grecaptcha.reset(widgetId);
      widgets.delete(containerId);

      update((current) => {
        const rest = { ...current };

        delete rest[containerId];

        return rest;
      });
    }
  }

  function getRecaptchaToken(containerId: string): string | undefined {
    return get({ subscribe })[containerId];
  }

  return {
    subscribe,
    enabled,
    initRecaptchaScript,
    initRecaptchaContainer,
    destroyRecaptchaWidget,
    executeRecaptchaContainer,
    getRecaptchaToken,
  };
}

export const recaptchaModel = createRecaptchaModel();
export const recaptchaLoaded = createLoadedStore();
